import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CheckIcon, XMarkIcon, StarIcon } from '@heroicons/react/24/solid';
import { FILTER_STATES } from '../../utils/triStateFilterUtils';

const TriStateCheckbox = ({ label, state, onChange, onLongClick, disabled = false }) => {
  const [pressTimer, setPressTimer] = useState(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const longPressThreshold = 500;

  const handleMouseDown = () => {
    if (disabled || !onLongClick) return;
    setIsLongPressing(false);
    const timer = setTimeout(() => {
      setIsLongPressing(true);
      onLongClick();
    }, longPressThreshold);
    setPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
      // Only trigger regular click if long press didn't activate
      if (!isLongPressing) {
        handleClick();
      }
      setIsLongPressing(false);
    } else {
      // No timer means onLongClick is not provided, use regular click
      handleClick();
    }
  };

  const handleMouseLeave = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
      setIsLongPressing(false);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    // Regular tri-state cycle
    if (state === FILTER_STATES.NEUTRAL) {
      onChange(FILTER_STATES.INCLUDE);
    } else if (state === FILTER_STATES.INCLUDE) {
      onChange(FILTER_STATES.EXCLUDE);
    } else if (state === FILTER_STATES.EXCLUDE) {
      onChange(FILTER_STATES.NEUTRAL);
    } else if (state === FILTER_STATES.ONLY) {
      // If in ONLY state, clicking returns to neutral (same as long-clicking again)
      if (onLongClick) {
        onLongClick(); // This will clear the ONLY state
      }
    }
  };

  const getCheckboxStyle = () => {
    if (state === FILTER_STATES.INCLUDE) return 'bg-green-100 border-green-600';
    if (state === FILTER_STATES.EXCLUDE) return 'bg-red-100 border-red-600';
    if (state === FILTER_STATES.ONLY) return 'bg-purple-100 border-purple-600';
    return 'bg-white border-neutral-sec';
  };

  const getIcon = () => {
    if (state === FILTER_STATES.INCLUDE) return <CheckIcon className="h-3 w-3 text-green-700" />;
    if (state === FILTER_STATES.EXCLUDE) return <XMarkIcon className="h-3 w-3 text-red-700" />;
    if (state === FILTER_STATES.ONLY) return <StarIcon className="h-3 w-3 text-purple-700" />;
    return null;
  };

  return (
    <label className={`flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
    }`}>
      <div
        role="checkbox"
        aria-checked={
          state === FILTER_STATES.INCLUDE ? 'true' :
          state === FILTER_STATES.EXCLUDE ? 'mixed' :
          state === FILTER_STATES.ONLY ? 'true' :
          'false'
        }
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className={`h-5 w-5 rounded border-2 flex items-center justify-center ${getCheckboxStyle()} ${!disabled && 'hover:border-brand-sec'}`}
      >
        {getIcon()}
      </div>
      <span>{label}</span>
    </label>
  );
};

TriStateCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  state: PropTypes.oneOf([
    FILTER_STATES.NEUTRAL,
    FILTER_STATES.INCLUDE,
    FILTER_STATES.EXCLUDE,
    FILTER_STATES.ONLY
  ]),
  onChange: PropTypes.func.isRequired,
  onLongClick: PropTypes.func, // Optional long-click handler
  disabled: PropTypes.bool
};

export default TriStateCheckbox;
